import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ExternalLink, Play, Users, Clock, MapPin, Phone, Mail, ChevronDown, ChevronUp } from 'lucide-react';

import { BACKEND_URL } from '../config/api';

// Service data from eliteplayerperformance.co.uk
const SERVICES = [
  {
    id: 'one-to-one',
    title: 'One-to-One Coaching',
    price: '£65',
    priceNote: 'per session',
    description: 'Take your performance to the next level with personalised one-to-one Speed & Agility Coaching.',
    features: [
      'Explosive acceleration development',
      'Quick change-of-direction skills',
      'Efficient movement mechanics',
      'Cutting-edge drills and proven training methods',
      'Customised to your goals and sport-specific needs'
    ],
    bookingUrl: 'https://eliteplayerperformance.setmore.com/book?step=staff&products=athlete-growth-pro&type=class',
    highlight: true
  },
  {
    id: 'squad-analyser',
    title: 'Squad Analyser',
    price: '£20',
    priceNote: 'per athlete (min 10)',
    description: 'Elite-Level Team Testing for 10+ Athletes in a single session. Complete high-performance testing solution for teams, academies, schools, and coaching groups.',
    features: [
      'Acceleration & Speed Testing (5m, 10m, 20m, 30m splits)',
      'Max Speed Sprint Analysis',
      'Change of Direction Testing (Pro Agility, T-Test, Multi-Angle Cuts)',
      'Deceleration Testing (ADA Test, 5-0-5 Test)',
      'Jump Performance Testing (CMJ, Squat Jump, Single-Leg)',
      'Full Individual Reports + Squad Comparison Dashboard'
    ],
    bookingUrl: 'https://eliteplayerperformance.setmore.com/classes/5ecbf136-2681-4f06-9bbf-0013fe4956ec',
    highlight: false
  },
  {
    id: 'strength-conditioning',
    title: 'Strength & Conditioning Classes',
    price: '£10',
    priceNote: 'per session',
    description: 'Youth Strength & Conditioning (Under 16s) - Build strong, confident bodies in a safe, supportive, and fun environment.',
    features: [
      'Fundamental movement skills development',
      'Coordination, balance, and age-appropriate strength',
      'Bodyweight exercises and light resistance',
      'Proper technique and injury prevention focus',
      'Builds confidence and positive attitude toward exercise'
    ],
    bookingUrl: 'https://eliteplayerperformance.setmore.com/classes/06a7fa66-d5c2-4970-a60d-27b37a8c4e5d',
    highlight: false
  },
  {
    id: 'speed-agility',
    title: 'Speed, Agility, Quickness Classes',
    price: '£10',
    priceNote: 'per session',
    description: 'Weekly Youth Speed Clinic (Under 16s) - Developing Faster, Stronger & More Confident Young Athletes.',
    features: [
      'Plyometric Training for explosive power',
      'Strength, Movement & Injury-Prevention Exercises',
      'Technical Sprint Drills',
      'Hill Running for Power & Acceleration',
      'Regular Sprint & Speed Assessments'
    ],
    bookingUrl: 'https://eliteplayerperformance.setmore.com/classes/429fd4f1-1f3b-4d20-acc0-4629a279170d',
    highlight: false
  },
  {
    id: 'yoga-mobility',
    title: 'Yoga & Mobility Classes',
    price: '£10',
    priceNote: 'per session',
    description: 'Sessions by The Art of Wellness - Enhance movement, support strength, and stay resilient.',
    features: [
      'Targeted mobility work',
      'Yoga-inspired movement',
      'Improve flexibility and joint range',
      'Balance and body awareness',
      'Recovery and injury prevention'
    ],
    bookingUrl: 'https://eliteplayerperformance.setmore.com/classes/34dd5119-9830-48dc-8f6b-9712f191f77f',
    highlight: false
  }
];

const ASSESSMENTS = [
  {
    title: 'Basic Performance Assessment',
    price: '£10',
    description: 'Entry-level assessment for athletes starting their performance journey.'
  },
  {
    title: 'Advance Performance Assessment',
    price: '£30',
    description: 'Comprehensive testing with detailed analysis and recommendations.'
  },
  {
    title: 'Elite Performance Assessment',
    price: '£50',
    description: 'Full test battery with complete biomechanical analysis and training plan.'
  },
  {
    title: 'Physiotherapy Consultation',
    price: '£70',
    description: 'New consultation with qualified physiotherapist.'
  }
];

const VIDEOS = [
  { category: 'Form Drills', description: 'Running technique and form exercises' },
  { category: 'Hurdle Drills', description: 'Hurdle training progressions' },
  { category: 'Mini Hurdle Drills', description: 'Quick feet and coordination' },
  { category: 'Stick Drills', description: 'Agility and footwork patterns' },
  { category: 'Cone Drills', description: 'Speed and direction changes' },
  { category: 'Box Drills (30cm)', description: 'Plyometric box exercises' },
  { category: 'Mat Exercises', description: 'Core and stability work' },
  { category: 'Obstacle Drills', description: 'Multi-directional movement' },
  { category: 'Step Exercises', description: 'Lower body power development' }
];

const LOCATIONS = [
  {
    name: 'Birmingham Alexander Stadium',
    description: 'Premier athletics venue, home of Birmingham 2022 Commonwealth Games'
  },
  {
    name: 'Vision Football Factory Tamworth',
    description: 'Indoor pitches: 4v4, 6v6, and 7v7 facilities'
  },
  {
    name: 'Sutton Coldfield Tennis Club',
    description: 'Quality facilities with friendly community atmosphere'
  }
];

export default function ServicesInfo() {
  const navigate = useNavigate();
  const [expandedService, setExpandedService] = useState('one-to-one');

  return (
    <div className="min-h-screen bg-[#050505] grid-texture">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-950/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-zinc-400 hover:text-white mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white uppercase tracking-tight">
                BE ELITE APP
              </h1>
              <p className="text-zinc-500 mt-1">
                Measure Athletic Abilities – Drive Peak Performance
              </p>
            </div>
            <a
              href="https://www.eliteplayerperformance.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#02b5ff] hover:text-white transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Visit Website
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 space-y-12">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#02b5ff]/20 to-[#ff025f]/20 border border-zinc-800 rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Setting New Standards for Athletic Performance
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto mb-6">
            Full test battery suitable for Adults and Juniors. Detailed assessments, analytics and performance data for athletes in all sports including Tennis, Cricket, Rugby, Football and more.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://eliteplayerperformance.setmore.com/book"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#02b5ff] hover:bg-[#0099dd] text-white font-bold px-6 py-3 rounded-full transition-colors"
              data-testid="book-now-btn"
            >
              Book Assessment
              <ExternalLink className="h-4 w-4" />
            </a>
            <a
              href="https://eliteplayerperformance.setmore.com/services/a33f36bc-782f-49e2-a14d-0286df78c540"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[#02b5ff] text-[#02b5ff] hover:bg-[#02b5ff]/10 font-bold px-6 py-3 rounded-full transition-colors"
            >
              Free Discovery Call
              <Phone className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Services Section */}
        <div>
          <h2 className="text-xl font-bold text-white uppercase tracking-tight mb-6 flex items-center gap-3">
            <div className="w-1 h-6 bg-[#02b5ff]"></div>
            Our Services
          </h2>
          <div className="space-y-4">
            {SERVICES.map((service) => (
              <div
                key={service.id}
                className={`bg-zinc-900/50 border rounded-lg overflow-hidden transition-all ${
                  service.highlight ? 'border-[#02b5ff]' : 'border-zinc-800'
                }`}
              >
                <button
                  onClick={() => setExpandedService(expandedService === service.id ? '' : service.id)}
                  className="w-full p-6 flex items-center justify-between text-left"
                  data-testid={`service-${service.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-bold text-white">{service.title}</h3>
                        {service.highlight && (
                          <span className="text-xs bg-[#ff025f] text-white px-2 py-1 rounded">
                            RECOMMENDED
                          </span>
                        )}
                      </div>
                      <p className="text-zinc-400 text-sm">{service.description.slice(0, 80)}...</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#02b5ff]">{service.price}</p>
                      <p className="text-xs text-zinc-500">{service.priceNote}</p>
                    </div>
                    {expandedService === service.id ? (
                      <ChevronUp className="h-5 w-5 text-zinc-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-zinc-400" />
                    )}
                  </div>
                </button>
                
                {expandedService === service.id && (
                  <div className="px-6 pb-6 border-t border-zinc-800">
                    <p className="text-zinc-300 mt-4 mb-4">{service.description}</p>
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="text-zinc-400 text-sm flex items-start gap-2">
                          <span className="text-[#02b5ff] mt-1">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <a
                      href={service.bookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#02b5ff] hover:bg-[#0099dd] text-white font-bold px-5 py-2 rounded-lg transition-colors"
                    >
                      Book Now
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Assessment Packages */}
        <div>
          <h2 className="text-xl font-bold text-white uppercase tracking-tight mb-6 flex items-center gap-3">
            <div className="w-1 h-6 bg-[#ff025f]"></div>
            Assessment Packages
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {ASSESSMENTS.map((assessment, idx) => (
              <div
                key={idx}
                className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-5 hover:border-zinc-700 transition-colors"
              >
                <h3 className="text-white font-bold mb-2">{assessment.title}</h3>
                <p className="text-2xl font-bold text-[#02b5ff] mb-2">{assessment.price}</p>
                <p className="text-zinc-400 text-sm">{assessment.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Training Videos Section */}
        <div>
          <h2 className="text-xl font-bold text-white uppercase tracking-tight mb-6 flex items-center gap-3">
            <div className="w-1 h-6 bg-[#02b5ff]"></div>
            Training Video Library
          </h2>
          <div className="bg-zinc-900/30 border border-zinc-800 rounded-lg p-6">
            <p className="text-zinc-400 mb-6">
              Access our comprehensive library of training drills and exercises. Perfect for home practice and skill development.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {VIDEOS.map((video, idx) => (
                <div
                  key={idx}
                  className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 flex items-center gap-3"
                >
                  <div className="w-10 h-10 bg-[#02b5ff]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Play className="h-4 w-4 text-[#02b5ff]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium text-sm">{video.category}</h4>
                    <p className="text-zinc-500 text-xs">{video.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 text-center">
              <a
                href="https://www.youtube.com/channel/UCDUlkVrrO5tnaJ8xpvgepxQ"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#02b5ff] hover:text-white transition-colors"
              >
                <Play className="h-4 w-4" />
                Watch on YouTube (Photon Sports)
              </a>
            </div>
          </div>
        </div>

        {/* Locations */}
        <div>
          <h2 className="text-xl font-bold text-white uppercase tracking-tight mb-6 flex items-center gap-3">
            <div className="w-1 h-6 bg-[#ff025f]"></div>
            Locations
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {LOCATIONS.map((location, idx) => (
              <div
                key={idx}
                className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-5"
              >
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#02b5ff] mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-bold mb-1">{location.name}</h3>
                    <p className="text-zinc-400 text-sm">{location.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-zinc-500 text-sm mt-4">
            West Midlands coverage including Birmingham, Walsall, Wolverhampton, Sandwell, Sutton Coldfield and Solihull
          </p>
        </div>

        {/* WhatsApp Community */}
        <div className="bg-gradient-to-r from-green-500/20 to-green-600/10 border border-green-500/30 rounded-xl p-8 text-center">
          <h2 className="text-xl font-bold text-white mb-3">Join Our Community</h2>
          <p className="text-zinc-400 mb-6">
            Be supported through your elite journey with our WhatsApp Community. Access latest offers, exclusive events and more.
          </p>
          <a
            href="https://chat.whatsapp.com/GtvNjx1xhUb0dJIBXsEY76"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white font-bold px-6 py-3 rounded-full transition-colors"
            data-testid="join-whatsapp-btn"
          >
            Join WhatsApp Community
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>

        {/* Contact */}
        <div className="bg-zinc-900/30 border border-zinc-800 rounded-lg p-6">
          <h2 className="text-lg font-bold text-white uppercase tracking-tight mb-4">Contact Us</h2>
          <div className="flex flex-wrap gap-6">
            <a
              href="mailto:bookings@eliteplayerperformance.co.uk"
              className="flex items-center gap-2 text-zinc-400 hover:text-[#02b5ff] transition-colors"
            >
              <Mail className="h-4 w-4" />
              bookings@eliteplayerperformance.co.uk
            </a>
            <a
              href="https://www.instagram.com/eliteplayerperformance"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-zinc-400 hover:text-[#02b5ff] transition-colors"
            >
              Instagram
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61584327089565"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-zinc-400 hover:text-[#02b5ff] transition-colors"
            >
              Facebook
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
